import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiActivity,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiCompass,
  FiEye,
  FiGlobe,
  FiMonitor,
  FiRefreshCw,
  FiSmartphone,
  FiTrendingDown,
  FiTrendingUp,
  FiUser,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import './AdminAnalytics.css';

const PAGE_NAMES = {
  '/': 'صفحه اصلی',
  '/services': 'خدمات',
  '/products': 'محصولات',
  '/articles': 'مقالات',
  '/about': 'درباره ما',
  '/contact': 'تماس با ما',
  '/login': 'ورود',
  '/register': 'ثبت‌نام',
  '/user-panel': 'پنل کاربری',
};
const DEVICE_NAMES = { desktop: 'دسکتاپ', mobile: 'موبایل', tablet: 'تبلت' };
const rangeOptions = [
  { value: 7, label: '۷ روز' },
  { value: 30, label: '۳۰ روز' },
  { value: 90, label: '۹۰ روز' },
];
const faNumber = (value) => Number(value || 0).toLocaleString('fa-IR');
const normalizePagePath = (path) => {
  const cleanPath = String(path || '/').split('?')[0];
  const parts = cleanPath.split('/').filter(Boolean);
  if (parts[0] && /^[a-z]{2,3}(?:-[a-z]{2})?$/i.test(parts[0])) parts.shift();
  return `/${parts.join('/')}` || '/';
};
const pageName = (path, title = '') => {
  const cleanPath = normalizePagePath(path);
  if (PAGE_NAMES[cleanPath]) return PAGE_NAMES[cleanPath];
  const specificTitle = String(title || '').trim();
  if (cleanPath.startsWith('/products/')) return specificTitle || 'جزئیات محصول';
  if (cleanPath.startsWith('/articles/')) return specificTitle || 'مطالعه مقاله';
  if (cleanPath.startsWith('/projects/')) return specificTitle || 'نمونه‌کار';
  return specificTitle || cleanPath;
};
const countryName = (countryCode, fallback = '') => {
  if (!countryCode) return fallback;
  try {
    return new Intl.DisplayNames(['fa'], { type: 'region' }).of(String(countryCode).toUpperCase()) || fallback;
  } catch (_) {
    return fallback || countryCode;
  }
};
const countryFlag = (countryCode) => {
  const code = String(countryCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(...code.split('').map((letter) => 127397 + letter.charCodeAt(0)));
};
const locationName = (item = {}) => {
  const country = countryName(item.countryCode, item.country);
  if (!country) return 'کشور نامشخص';
  return [countryFlag(item.countryCode), country].filter(Boolean).join(' ');
};
const ipLabel = (item = {}) => item.ipAddress || 'IP نامشخص';
const timeAgo = (value) => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 15) return 'همین حالا';
  if (seconds < 60) return `${faNumber(seconds)} ثانیه پیش`;
  if (seconds < 3600) return `${faNumber(Math.floor(seconds / 60))} دقیقه پیش`;
  return `${faNumber(Math.floor(seconds / 3600))} ساعت پیش`;
};
const sourceName = (source) => {
  if (!source) return 'ورود مستقیم';
  try {
    const host = new URL(source).hostname.replace(/^www\./, '');
    return host || 'ورود مستقیم';
  } catch (_) {
    return 'ورود مستقیم';
  }
};
const deviceIcon = (device) => (device === 'mobile' ? <FiSmartphone /> : <FiMonitor />);

const TrendChart = ({ data }) => {
  const width = 720;
  const height = 230;
  const padX = 18;
  const padY = 20;
  const max = Math.max(...data.map((item) => item.views), 1);
  const points = data.map((item, index) => ({
    ...item,
    x: padX + (index * (width - padX * 2)) / Math.max(data.length - 1, 1),
    y: height - padY - (item.views / max) * (height - padY * 2),
  }));
  const line = points.map((point) => `${point.x},${point.y}`).join(' ');
  const area = `${padX},${height - padY} ${line} ${width - padX},${height - padY}`;
  const labelStep = data.length > 14 ? Math.ceil(data.length / 7) : 1;

  return (
    <div className="analytics-chart-wrap">
      <svg className="analytics-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="نمودار بازدید">
        <defs>
          <linearGradient id="analyticsArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00b4d8" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#00b4d8" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((ratio) => (
          <line key={ratio} x1="0" x2={width} y1={height * ratio - 5} y2={height * ratio - 5} className="analytics-grid-line" />
        ))}
        <polygon points={area} fill="url(#analyticsArea)" />
        <polyline points={line} className="analytics-chart-line" />
        {points.map((point, index) => (
          <g key={point.date}>
            <circle cx={point.x} cy={point.y} r="10" className="analytics-chart-hit" />
            <circle cx={point.x} cy={point.y} r="4" className="analytics-chart-dot" />
            <title>{`${new Date(point.date).toLocaleDateString('fa-IR')}: ${faNumber(point.views)} بازدید`}</title>
            {(index % labelStep === 0 || index === points.length - 1) && (
              <text x={point.x} y={height - 1} className="analytics-chart-label">
                {new Date(point.date).toLocaleDateString('fa-IR', { month: 'short', day: 'numeric' })}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
};

const AdminAnalytics = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const response = await api.get(`/api/analytics/overview?days=${days}`);
      setData(response.data);
    } catch (requestError) {
      setError(requestError.message || 'دریافت آمار انجام نشد');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days]);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load(true), 10000);
    return () => window.clearInterval(timer);
  }, [load]);

  const loadDetails = useCallback(async (type, page = 1) => {
    setDetailsLoading(true);
    setDetailsError('');
    try {
      const response = await api.get(`/api/analytics/details?type=${type}&days=${days}&page=${page}`);
      setDetails(response.data);
    } catch (requestError) {
      setDetailsError(requestError.message || 'دریافت جزئیات انجام نشد');
    } finally {
      setDetailsLoading(false);
    }
  }, [days]);

  const openDetails = (type) => {
    setDetails({ type, items: [], pagination: { page: 1, totalPages: 1, total: 0 } });
    loadDetails(type, 1);
  };

  const closeDetails = useCallback(() => {
    setDetails(null);
    setDetailsError('');
  }, []);

  useEffect(() => {
    if (!details) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeDetails();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [details, closeDetails]);

  const maxPageViews = useMemo(
    () => Math.max(...(data?.topPages || []).map((item) => item.views), 1),
    [data]
  );
  const totalDevices = useMemo(
    () => (data?.devices || []).reduce((sum, item) => sum + item.value, 0) || 1,
    [data]
  );

  if (loading) {
    return (
      <div className="analytics-loading" aria-live="polite">
        <span /><span /><span />
        <p>در حال ساخت تصویر زنده از سایت...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="analytics-error">
        <FiActivity />
        <h2>آمار در دسترس نیست</h2>
        <p>{error}</p>
        <button type="button" onClick={() => load()}><FiRefreshCw /> تلاش دوباره</button>
      </div>
    );
  }

  const summary = data?.summary || {};
  const changeIsPositive = summary.change >= 0;

  return (
    <div className="admin-analytics">
      <section className="analytics-command">
        <div className="analytics-command-copy">
          <h2>آمار سایت</h2>
        </div>
        <div className="analytics-command-actions">
          <div className="analytics-range" aria-label="بازه گزارش">
            {rangeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={days === option.value ? 'active' : ''}
                onClick={() => setDays(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button type="button" className="analytics-refresh" onClick={() => load(true)} aria-label="به‌روزرسانی آمار">
            <FiRefreshCw className={refreshing ? 'is-spinning' : ''} />
          </button>
        </div>
      </section>

      <section className="analytics-kpis">
        <article className="analytics-kpi is-live">
          <span className="analytics-kpi-icon"><FiActivity /></span>
          <div><strong>{faNumber(summary.online)}</strong><span>نفر آنلاین</span></div>
          <small><i /> در ۹۰ ثانیه اخیر</small>
        </article>
        <article className="analytics-kpi">
          <span className="analytics-kpi-icon"><FiEye /></span>
          <div><strong>{faNumber(summary.todayViews)}</strong><span>بازدید امروز</span></div>
          <small className={changeIsPositive ? 'is-up' : 'is-down'}>
            {changeIsPositive ? <FiTrendingUp /> : <FiTrendingDown />}
            {faNumber(Math.abs(summary.change))}٪ نسبت به دیروز
          </small>
        </article>
        <article className="analytics-kpi">
          <span className="analytics-kpi-icon"><FiUsers /></span>
          <div><strong>{faNumber(summary.uniqueVisitors)}</strong><span>بازدیدکننده یکتا</span></div>
          <small>در {faNumber(days)} روز اخیر</small>
        </article>
        <article className="analytics-kpi">
          <span className="analytics-kpi-icon"><FiCompass /></span>
          <div><strong>{faNumber(summary.totalViews)}</strong><span>نمایش صفحه</span></div>
          <small>مجموع بازدید بازه</small>
        </article>
      </section>

      <div className="analytics-primary-grid">
        <section className="analytics-panel analytics-trend-panel">
          <header className="analytics-panel-head">
            <div>
              <span>روند ترافیک</span>
              <h3>بازدید روزانه</h3>
            </div>
            <span className="analytics-panel-badge">{faNumber(days)} روز اخیر</span>
          </header>
          <TrendChart data={data?.trend || []} />
        </section>

        <section className="analytics-panel analytics-online-panel">
          <header className="analytics-panel-head">
            <div>
              <span>حضور لحظه‌ای</span>
              <h3>الان کجا هستند؟</h3>
            </div>
            <span className="analytics-online-count"><i /> {faNumber(summary.online)}</span>
          </header>
          <div className="analytics-online-list">
            {(data?.onlineSessions || []).length === 0 ? (
              <div className="analytics-empty"><FiGlobe /><p>فعلاً کسی در سایت نیست.</p></div>
            ) : data.onlineSessions.map((session) => (
              <article key={session._id} className="analytics-online-row">
                <span className="analytics-user-avatar">
                  {session.user?.avatar ? <img src={session.user.avatar} alt="" /> : <FiUser />}
                  <i />
                </span>
                <div className="analytics-online-main">
                  <strong>{session.user?.name || 'بازدیدکننده مهمان'}</strong>
                  <span>{pageName(session.pagePath, session.pageTitle)}</span>
                  <small className="analytics-location-line"><FiGlobe /> {locationName(session)} · <b dir="ltr">{ipLabel(session)}</b></small>
                </div>
                <div className="analytics-online-meta">
                  <span>{deviceIcon(session.device)} {DEVICE_NAMES[session.device] || session.device}</span>
                  <small>{timeAgo(session.lastSeen)}</small>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>

      <div className="analytics-secondary-grid">
        <section className="analytics-panel">
          <header className="analytics-panel-head">
            <div><span>محتوای محبوب</span><h3>صفحات پربازدید</h3></div>
            <button type="button" className="analytics-view-all" onClick={() => openDetails('pages')}>مشاهده همه</button>
          </header>
          <div className="analytics-pages">
            {(data?.topPages || []).map((page, index) => (
              <article key={page.path}>
                <span className="analytics-page-rank">{faNumber(index + 1)}</span>
                <div>
                  <strong>{pageName(page.path, page.title)}</strong>
                  <small dir="ltr">{page.path}</small>
                  <span><i style={{ width: `${(page.views / maxPageViews) * 100}%` }} /></span>
                </div>
                <b>{faNumber(page.views)}<small> بازدید</small></b>
              </article>
            ))}
            {!data?.topPages?.length && <div className="analytics-empty compact"><p>هنوز داده‌ای ثبت نشده است.</p></div>}
          </div>
        </section>

        <section className="analytics-panel">
          <header className="analytics-panel-head">
            <div><span>نوع دستگاه</span><h3>مخاطبان چگونه می‌بینند؟</h3></div>
          </header>
          <div className="analytics-devices">
            {(data?.devices || []).map((device, index) => {
              const percent = Math.round((device.value / totalDevices) * 100);
              return (
                <article key={device.name}>
                  <div className={`analytics-device-icon tone-${index + 1}`}>{deviceIcon(device.name)}</div>
                  <div><strong>{DEVICE_NAMES[device.name] || device.name}</strong><span>{faNumber(device.value)} بازدید</span></div>
                  <b>{faNumber(percent)}٪</b>
                  <span className="analytics-device-bar"><i style={{ width: `${percent}%` }} /></span>
                </article>
              );
            })}
          </div>
          <div className="analytics-sources">
            <h4>مسیرهای ورود</h4>
            {(data?.referrers || []).slice(0, 4).map((source) => (
              <div key={source.source || 'direct'}>
                <span><FiGlobe /> {sourceName(source.source)}</span>
                <strong>{faNumber(source.value)}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="analytics-panel analytics-feed-panel">
          <header className="analytics-panel-head">
            <div><span>آخرین حرکت‌ها</span><h3>جریان زنده بازدید</h3></div>
            <button type="button" className="analytics-view-all" onClick={() => openDetails('views')}>مشاهده همه</button>
          </header>
          <div className="analytics-feed">
            {(data?.recentViews || []).map((view, index) => (
              <article key={`${view._id}-${index}`}>
                <span className="analytics-feed-dot"><i /></span>
                <div>
                  <strong>{view.user?.name || 'یک مهمان'}</strong>
                  <p>وارد <b>{pageName(view.pagePath, view.pageTitle)}</b> شد</p>
                  <small><FiClock /> {timeAgo(view.occurredAt)} · {view.browser}</small>
                  <small className="analytics-location-line"><FiGlobe /> {locationName(view)} · <b dir="ltr">{ipLabel(view)}</b></small>
                </div>
              </article>
            ))}
            {!data?.recentViews?.length && <div className="analytics-empty compact"><p>اولین بازدید به‌زودی اینجا دیده می‌شود.</p></div>}
          </div>
        </section>
      </div>

      {details && (
        <div className="analytics-modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeDetails();
        }}>
          <section className="analytics-modal" role="dialog" aria-modal="true" aria-labelledby="analytics-details-title">
            <header className="analytics-modal-head">
              <div>
                <span>{faNumber(days)} روز اخیر</span>
                <h3 id="analytics-details-title">
                  {details.type === 'pages' ? 'همه صفحات پربازدید' : 'همه بازدیدهای اخیر'}
                </h3>
              </div>
              <button type="button" className="ui-close-button" onClick={closeDetails} aria-label="بستن"><FiX /></button>
            </header>

            <div className="analytics-modal-body">
              {detailsLoading ? (
                <div className="analytics-details-status"><FiRefreshCw className="is-spinning" /> در حال دریافت اطلاعات</div>
              ) : detailsError ? (
                <div className="analytics-details-status is-error">
                  <span>{detailsError}</span>
                  <button type="button" onClick={() => loadDetails(details.type, details.pagination?.page || 1)}>تلاش دوباره</button>
                </div>
              ) : details.type === 'pages' ? (
                <div className="analytics-details-pages">
                  {details.items.map((item, index) => (
                    <article key={item.path}>
                      <span>{faNumber(((details.pagination.page - 1) * details.pagination.pageSize) + index + 1)}</span>
                      <div><strong>{pageName(item.path, item.title)}</strong><small dir="ltr">{item.path}</small></div>
                      <div><b>{faNumber(item.views)}</b><small>بازدید</small></div>
                      <div><b>{faNumber(item.visitors)}</b><small>بازدیدکننده</small></div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="analytics-details-feed">
                  {details.items.map((view, index) => (
                    <article key={`${view._id}-${index}`}>
                      <span className="analytics-feed-dot"><i /></span>
                      <div>
                        <strong>{view.user?.name || 'یک مهمان'}</strong>
                        <p>وارد <b>{pageName(view.pagePath, view.pageTitle)}</b> شد</p>
                        <small><FiClock /> {timeAgo(view.occurredAt)} · {view.browser} · {DEVICE_NAMES[view.device] || view.device}</small>
                        <small className="analytics-location-line"><FiGlobe /> {locationName(view)} · <b dir="ltr">{ipLabel(view)}</b></small>
                        <small className="analytics-path-line" dir="ltr">{view.pagePath}</small>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {!detailsLoading && !detailsError && details.items.length === 0 && (
                <div className="analytics-empty"><p>داده‌ای برای نمایش وجود ندارد.</p></div>
              )}
            </div>

            <footer className="analytics-modal-footer">
              <span>مجموع {faNumber(details.pagination?.total)} مورد</span>
              <div>
                <button
                  type="button"
                  disabled={detailsLoading || details.pagination?.page <= 1}
                  onClick={() => loadDetails(details.type, details.pagination.page - 1)}
                >
                  <FiChevronRight /> قبلی
                </button>
                <span>صفحه {faNumber(details.pagination?.page)} از {faNumber(details.pagination?.totalPages)}</span>
                <button
                  type="button"
                  disabled={detailsLoading || details.pagination?.page >= details.pagination?.totalPages}
                  onClick={() => loadDetails(details.type, details.pagination.page + 1)}
                >
                  بعدی <FiChevronLeft />
                </button>
              </div>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;
